import React, { useState, useRef } from "react";
import YouTube from "react-youtube";

export default function LiveSingCoach() {
  const [listening, setListening] = useState(false);
  const [volume, setVolume] = useState(0);
  const [feedback, setFeedback] = useState("🎤 Ready to sing");
  const [finalReview, setFinalReview] = useState("");
  const [audioURL, setAudioURL] = useState("");
  const [youtubeUrl, setYoutubeUrl] = useState("");
  const [videoId, setVideoId] = useState("");
  const [singerName, setSingerName] = useState("");
  const [logo, setLogo] = useState("");

  const [pitch, setPitch] = useState(0);
  const [note, setNote] = useState("");
  const [score, setScore] = useState(0);
  const [report, setReport] = useState(null);

  const audioContextRef = useRef(null);
  const streamRef = useRef(null);
  const intervalRef = useRef(null);

  const scoresRef = useRef([]);
  const pitchRef = useRef([]);

  const mediaRecorderRef = useRef(null);
  const audioChunksRef = useRef([]);

  const notes = [
    { n: "Sa / C", f: 130 },
    { n: "Re / D", f: 146 },
    { n: "Ga / E", f: 164 },
    { n: "Ma / F", f: 174 },
    { n: "Pa / G", f: 196 },
    { n: "Dha / A", f: 220 },
    { n: "Ni / B", f: 246 },
  ];

  const getNote = (freq) => {
    let closest = notes[0];
    let minDiff = Infinity;

    for (let i of notes) {
      const diff = Math.abs(i.f - freq);
      if (diff < minDiff) {
        minDiff = diff;
        closest = i;
      }
    }
    return closest.n;
  };

  const loadVideo = () => {
    const match = youtubeUrl.match(/(?:v=|youtu\.be\/)([^&]+)/);
    if (match) setVideoId(match[1]);
    else alert("Invalid YouTube link");
  };

  const detectPitch = (buffer, sampleRate) => {
    let max = 0;
    let freq = 0;

    for (let i = 0; i < buffer.length; i++) {
      const val = Math.abs(buffer[i]);
      if (val > max) {
        max = val;
        freq = (i * sampleRate) / buffer.length;
      }
    }

    return freq;
  };

  const startListening = async () => {
    try {
      setFinalReview("");
      setScore(0);
      setReport(null);

      scoresRef.current = [];
      pitchRef.current = [];
      audioChunksRef.current = [];

      const screenStream = await navigator.mediaDevices.getDisplayMedia({
        video: true,
        audio: true,
      });

      const micStream = await navigator.mediaDevices.getUserMedia({
        audio: true,
      });

      const audioContext = new AudioContext();
      audioContextRef.current = audioContext;

      const destination = audioContext.createMediaStreamDestination();

      if (screenStream.getAudioTracks().length) {
        const screenSource =
          audioContext.createMediaStreamSource(screenStream);

        const screenGain = audioContext.createGain();
        screenGain.gain.value = 1.8;

        screenSource.connect(screenGain);
        screenGain.connect(destination);
      }

      const micSource =
        audioContext.createMediaStreamSource(micStream);

      const micGain = audioContext.createGain();
      micGain.gain.value = 10.0;

      micSource.connect(micGain);
      micGain.connect(destination);

      const combinedStream = destination.stream;

      streamRef.current = { screenStream, micStream };

      const recorder = new MediaRecorder(combinedStream);
      mediaRecorderRef.current = recorder;

      recorder.ondataavailable = (e) =>
        audioChunksRef.current.push(e.data);

      recorder.onstop = () => {
        const blob = new Blob(audioChunksRef.current, {
          type: "audio/webm",
        });
        setAudioURL(URL.createObjectURL(blob));
      };

      recorder.start();

      const analyser = audioContext.createAnalyser();
      const source =
        audioContext.createMediaStreamSource(combinedStream);

      source.connect(analyser);
      analyser.fftSize = 2048;

      const buffer = new Float32Array(analyser.fftSize);

      intervalRef.current = setInterval(() => {
        analyser.getFloatTimeDomainData(buffer);

        const avg =
          buffer.reduce((a, b) => a + Math.abs(b), 0) /
          buffer.length;

        const currentVolume = Math.min(100, avg * 420);

        setVolume(currentVolume);
        scoresRef.current.push(currentVolume);

        const freq = detectPitch(buffer, audioContext.sampleRate);

        if (freq > 50 && freq < 1000) {
          const detected = getNote(freq);
          setPitch(Math.round(freq));
          setNote(detected);
          pitchRef.current.push(freq);
        }

        const avgVol =
          scoresRef.current.slice(-10).reduce((a, b) => a + b, 0) /
          (scoresRef.current.slice(-10).length || 1);

        if (avgVol < 20) {
          setFeedback("🔈 Sing louder");
        } else if (avgVol < 55) {
          setFeedback("🎯 Good control");
        } else {
          setFeedback("🔥 Strong voice detected");
        }

        if (freq > 0 && Math.abs(freq - 174) > 70) {
          setFeedback("⚠️ Off pitch — adjust note!");
        }
      }, 120);

      setListening(true);
    } catch (err) {
      alert("Allow mic + screen share permissions");
    }
  };

  const stopListening = () => {
    mediaRecorderRef.current?.stop();

    if (streamRef.current) {
      streamRef.current.screenStream?.getTracks().forEach((t) => t.stop());
      streamRef.current.micStream?.getTracks().forEach((t) => t.stop());
    }

    audioContextRef.current?.close();
    clearInterval(intervalRef.current);

    const avgVol =
      scoresRef.current.reduce((a, b) => a + b, 0) /
      (scoresRef.current.length || 1);

    const avgPitch =
      pitchRef.current.reduce((a, b) => a + b, 0) /
      (pitchRef.current.length || 1);

    let finalScore = 0;

    if (avgVol > 35) finalScore += 40;
    if (avgPitch > 100) finalScore += 30;
    if (avgPitch < 500) finalScore += 30;

    setScore(finalScore);

    let review = "";

    if (finalScore > 80) {
      review = "🏆 SUPERSTAR LEVEL";
    } else if (finalScore > 60) {
      review = "🔥 Very Strong Singer";
    } else if (finalScore > 40) {
      review = "🎯 Good — improve pitch stability";
    } else {
      review = "🎤 Beginner — needs practice";
    }

    setFinalReview(review);

    setReport({
      songLink: youtubeUrl,
      score: finalScore,
      avgVolume: avgVol.toFixed(2),
      avgPitch: avgPitch.toFixed(2),
      note,
      review,
      session: new Date().toLocaleString(),
      breathControl: Math.min(100, Math.round(avgVol * 1.5)),
      pitchStability: Math.min(100, Math.round(avgPitch / 4)),
      confidence: Math.min(100, Math.round((avgVol + avgPitch / 5) / 2)),
    });

    setListening(false);
    setVolume(0);
    setFeedback("🎤 Session Complete");
  };

  const downloadCertificate = () => {
  const cert = `
    <html>
    <head>
      <style>
        @page {
          size: A4 landscape;
          margin: 0;
        }

        html, body {
          width: 297mm;
          height: 210mm;
          margin: 0;
          padding: 0;
          overflow: hidden;
          -webkit-print-color-adjust: exact !important;
          print-color-adjust: exact !important;
        }

        body {
  background: #111827;
  display: flex;
  align-items: center;
  justify-content: center;
  height: 100vh;
  margin: 0;
  font-family: Georgia, serif;
}

       .certificate {
  width: 95%;
  height: 95vh;
  background:
    radial-gradient(circle at top left, rgba(250,204,21,0.15), transparent 35%),
    radial-gradient(circle at bottom right, rgba(34,197,94,0.12), transparent 35%),
    linear-gradient(135deg,#0f172a,#1e293b,#111827);

  border: 10px solid #facc15;
  border-radius: 24px;

  position: relative;
  padding: 14px;

  box-sizing: border-box;
  color: white;
  text-align: center;

  overflow: hidden;
}

        .inner {
  border: 3px solid rgba(250,204,21,0.55);
  border-radius: 18px;

  height: calc(100% - 10px);
  padding: 10px;

  box-sizing: border-box;

  display: flex;
  flex-direction: column;
  justify-content: space-between;

  overflow: hidden;
}

        .corner {
          position: absolute;
          width: 45px;
          height: 45px;
          border: 4px solid #facc15;
        }

        .tl { top:18px; left:18px; border-right:none; border-bottom:none; }
        .tr { top:18px; right:18px; border-left:none; border-bottom:none; }
        .bl { bottom:18px; left:18px; border-right:none; border-top:none; }
        .br { bottom:18px; right:18px; border-left:none; border-top:none; }

        .title {
          font-size: 30px;
          color: #facc15;
          font-weight: bold;
          letter-spacing: 2px;
          margin-top: 8px;
        }

        .subtitle {
          font-size: 18px;
          color: #e5e7eb;
          margin: 8px 0 18px;
          font-style: italic;
        }

        .box {
          background: rgba(255,255,255,0.05);
          border: 2px solid #334155;
          border-radius: 16px;
          padding: 8px;
          margin: 6px auto;
        }

        .line {
          font-size: 14px;
          margin: 2px 0;
        }

        .review {
          font-size: 26px;
          color: #22c55e;
          font-weight: bold;
          margin-top: 14px;
        }

        .footer {
          margin-top: 10px;
          border-top: 2px solid #475569;
          padding-top: 12px;
        }

        .brand {
          font-size: 24px;
          color: #22c55e;
          font-weight: bold;
          letter-spacing: 5px;
        }

        .small {
          font-size: 14px;
          color: #cbd5e1;
        }
      </style>
    </head>

    <body>
      <div class="certificate">

        <div class="corner tl"></div>
        <div class="corner tr"></div>
        <div class="corner bl"></div>
        <div class="corner br"></div>

        <div class="inner">
          ${
  logo
    ? `<img src="${logo}" style="
width:60px;
height:60px;
border-radius:50%;
margin-bottom:10px;
object-fit:cover;
border:3px solid #facc15;
box-shadow:0 0 14px rgba(250,204,21,0.5);
" />`
    : ""
}
          <div class="title">VEDAI PERFORMANCE CERTIFICATE</div>
          <div class="subtitle">
            Official Recognition of Vocal Excellence
          </div>
          <div style="
font-size:18px;
color:#e5e7eb;
margin-top:10px;
">
Proudly Awarded To
</div>

<div style="
font-size:22px;
color:#22c55e;
font-weight:bold;
margin-bottom:15px;
">
${singerName || "VedAI Singer"}
</div>
          

          <div class="box">
            <div class="line">🎵 Song: ${report.songLink}</div>
            <div class="line">🏆 Score: ${report.score}/100</div>
            <div class="line">🎶 Note: ${report.note}</div>
            <div class="line">🌬 Breath Control: ${report.breathControl}%</div>
            <div class="line">📈 Pitch Stability: ${report.pitchStability}%</div>
            <div class="line">🔥 Confidence: ${report.confidence}%</div>
          </div>

          <div class="review">${report.review}</div>

<div class="box">
  <div class="line">
    ${
      report.breathControl > 75
        ? "✅ Excellent breath control"
        : "⚠ Practice long-note breathing"
    }
  </div>

  <div class="line">
    ${
      report.pitchStability > 75
        ? "✅ Stable pitch accuracy"
        : "⚠ Needs pitch consistency practice"
    }
  </div>

  <div class="line">
    ${
      report.confidence > 75
        ? "✅ Strong vocal confidence"
        : "⚠ Improve vocal projection"
    }
  </div>
</div>

          <div class="line">${report.session}</div>

          <div class="footer">
            <div class="brand">VEDAI</div>
            <div class="small">
              AI Certified Singing Performance Assessment
            </div>
          </div>

        </div>
      </div>

      <script>
        setTimeout(() => window.print(), 500);
      </script>
    </body>
    </html>
  `;

  const win = window.open("", "_blank");
  win.document.write(cert);
  win.document.close();
};

  return (
    <div
      style={{
        maxWidth: "900px",
        margin: "30px auto",
        padding: "20px",
        background: "#0f172a",
        color: "white",
        borderRadius: "20px",
      }}
    >
      <h1>🎤 NUKKAD PRO SINGER AI v3</h1>

<input
  style={{
    width: "100%",
    padding: "12px",
    borderRadius: "10px",
    marginBottom: "10px",
    background: "#1e293b",
    color: "white",
  }}
  placeholder="Enter Singer Name"
  value={singerName}
  onChange={(e) => setSingerName(e.target.value)}
/>

<input
  type="file"
  accept="image/*"
  onChange={(e) => {
    const file = e.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.onloadend = () => setLogo(reader.result);
      reader.readAsDataURL(file);
    }
  }}
  style={{ marginBottom: "12px", color: "white" }}
/>

{logo && (
  <div style={{ marginBottom: "15px", textAlign: "center" }}>
    <img
      src={logo}
      alt="VedAI Logo"
      style={{
        width: "80px",
        height: "80px",
        borderRadius: "50%",
        objectFit: "cover",
        border: "3px solid #facc15",
        boxShadow: "0 0 18px rgba(250,204,21,0.6)"
      }}
    />
  </div>
)}


      <input
        style={{
          width: "100%",
          padding: "12px",
          borderRadius: "10px",
          marginBottom: "10px",
          background: "#1e293b",
          color: "white",
        }}
        placeholder="Paste YouTube karaoke link"
        value={youtubeUrl}
        onChange={(e) => setYoutubeUrl(e.target.value)}
      />

      <button onClick={loadVideo}>Load Karaoke</button>

      {videoId && (
        <YouTube videoId={videoId} opts={{ width: "100%", height: "300" }} />
      )}

      <h2>{feedback}</h2>
      <h3>🎵 Pitch: {pitch} Hz | Note: {note}</h3>
      <h3>🏆 Score: {score}/100</h3>

      <div style={{ height: "20px", background: "#334155" }}>
        <div
          style={{
            width: `${volume}%`,
            height: "100%",
            background: "#22c55e",
          }}
        />
      </div>

      {!listening ? (
        <button onClick={startListening}>🎤 Start Singing</button>
      ) : (
        <button onClick={stopListening}>⏹ Stop Singing</button>
      )}

      {finalReview && <h2>{finalReview}</h2>}

      {audioURL && (
        <div style={{ marginTop: "20px" }}>
          <audio controls src={audioURL} />

          <br />

          <a
            href={audioURL}
            download={`vedai-recording-${Date.now()}.webm`}
          >
            <button>⬇ Download Recording</button>
          </a>
        </div>
      )}

      {report && (
        <div style={{ marginTop: "20px" }}>
          <button
            onClick={downloadCertificate}
            style={{
              padding: "14px 22px",
              background: "#facc15",
              color: "#000",
              fontWeight: "bold",
              borderRadius: "12px",
            }}
          >
            🏆 Download VedAI Certificate
          </button>
        </div>
      )}
    </div>
  );
}